/**
 * Service Learners — READ-ONLY
 * Les ajouts/modifications d'apprenants se font depuis le LMS
 */
import { supabase } from '../config/supabase';
import { Learner, LearnerWithDetails, LearnerFilters, Group } from '../types';

const toLearner = (row: Record<string, unknown>): Learner => ({
  id: row.id as string,
  organizationId: row.organization_id as string,
  structureId: row.structure_id as string | undefined,
  lmsUserId: row.lms_user_id as string | undefined,
  email: row.email as string,
  fullname: row.fullname as string,
  jobTitle: row.job_title as string | undefined,
  department: row.department as string | undefined,
  status: (row.status as Learner['status']) || 'active',
  enrolledAt: row.enrolled_at ? new Date(row.enrolled_at as string) : undefined,
  completedAt: row.completed_at ? new Date(row.completed_at as string) : undefined,
  createdAt: new Date(row.created_at as string),
  updatedAt: new Date(row.updated_at as string),
});

export const learnersService = {
  /**
   * Get learners with optional filters
   */
  async getAll(filters?: LearnerFilters): Promise<Learner[]> {
    let query = supabase
      .from('learners')
      .select('*')
      .order('fullname');

    if (filters?.organizationId) {
      query = query.eq('organization_id', filters.organizationId);
    }
    if (filters?.structureId) {
      query = query.eq('structure_id', filters.structureId);
    }
    if (filters?.status) {
      query = query.eq('status', filters.status);
    }
    if (filters?.search) {
      query = query.or(`fullname.ilike.%${filters.search}%,email.ilike.%${filters.search}%`);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching learners:', error);
      throw new Error('Impossible de charger les apprenants');
    }

    return (data || []).map(toLearner);
  },

  /**
   * Get learners for an organization with groups and enrollment progress.
   * Strategy: fetch learner IDs first (RPC), then use those IDs to scope
   * all subsequent queries. Avoids nested PostgREST selects that can fail
   * due to RLS cascading. Joins are done in JS.
   */
  async getByOrganization(organizationId: string): Promise<LearnerWithDetails[]> {
    // Step 1: Get learners via RPC (SECURITY DEFINER — bypasses RLS)
    const learnersResult = await supabase.rpc('get_learners_with_stats', { org_id: organizationId });

    if (learnersResult.error) {
      console.error('Error fetching learners via RPC:', learnersResult.error);
      throw new Error('Impossible de charger les apprenants');
    }

    const learnerRows = learnersResult.data || [];
    if (learnerRows.length === 0) return [];

    const learnerIds = learnerRows.map((r: Record<string, unknown>) => r.id as string);

    // Step 2: Parallel fetch of groups and enrollments, scoped to these learner IDs
    const [groupLearnersResult, groupsResult, enrollmentsResult, courseProgressResult] = await Promise.all([
      supabase
        .from('group_learners')
        .select('learner_id, group_id')
        .in('learner_id', learnerIds),
      supabase
        .from('groups')
        .select('*')
        .eq('organization_id', organizationId),
      supabase
        .from('enrollments')
        .select('id, learner_id, course_id, enrolled_at')
        .in('learner_id', learnerIds),
      // Fetch course_progress separately (avoids nested PostgREST + RLS cascade)
      // We'll match by enrollment_id in JS
      supabase
        .from('course_progress')
        .select('id, enrollment_id, progress_percent, completed_at'),
    ]);

    // Log errors but don't throw — graceful degradation
    if (groupLearnersResult.error) {
      console.error('Error fetching group_learners:', groupLearnersResult.error);
    }
    if (groupsResult.error) {
      console.error('Error fetching groups:', groupsResult.error);
    }
    if (enrollmentsResult.error) {
      console.error('Error fetching enrollments:', enrollmentsResult.error);
    }
    if (courseProgressResult.error) {
      console.error('Error fetching course_progress:', courseProgressResult.error);
    }

    // Build groups map: groupId -> Group
    const groupsMap = new Map<string, Group>();
    (groupsResult.data || []).forEach((g) => {
      groupsMap.set(g.id, {
        id: g.id,
        organizationId: g.organization_id,
        name: g.name,
        description: g.description,
        createdAt: new Date(g.created_at),
        updatedAt: new Date(g.updated_at),
      });
    });

    // Build learner->groups map
    const learnerGroupsMap = new Map<string, Group[]>();
    (groupLearnersResult.data || []).forEach((gl) => {
      const group = groupsMap.get(gl.group_id);
      if (!group) return;
      if (!learnerGroupsMap.has(gl.learner_id)) {
        learnerGroupsMap.set(gl.learner_id, []);
      }
      learnerGroupsMap.get(gl.learner_id)!.push(group);
    });

    // Build enrollment_id -> progress map
    const progressMap = new Map<string, { progressPercent: number; completedAt?: Date }>();
    (courseProgressResult.data || []).forEach((cp) => {
      progressMap.set(cp.enrollment_id, {
        progressPercent: cp.progress_percent || 0,
        completedAt: cp.completed_at ? new Date(cp.completed_at) : undefined,
      });
    });

    // Build learner->enrollments progress map
    const learnerProgressMap = new Map<string, { progressPercent: number; isCompleted: boolean }[]>();
    (enrollmentsResult.data || []).forEach((e) => {
      const cp = progressMap.get(e.id);
      const progressPercent = cp?.progressPercent || 0;
      const isCompleted = !!cp?.completedAt;

      if (!learnerProgressMap.has(e.learner_id)) {
        learnerProgressMap.set(e.learner_id, []);
      }
      learnerProgressMap.get(e.learner_id)!.push({ progressPercent, isCompleted });
    });

    // Build final result
    return learnerRows.map((row: Record<string, unknown>): LearnerWithDetails => {
      const learner = toLearner(row);
      const groups = learnerGroupsMap.get(learner.id) || [];
      const enrollments = learnerProgressMap.get(learner.id) || [];

      // Overall progress = average of all enrollment progress
      const overallProgress = enrollments.length > 0
        ? Math.round(enrollments.reduce((sum, e) => sum + e.progressPercent, 0) / enrollments.length)
        : 0;

      return {
        ...learner,
        groups,
        lastSignInAt: row.last_sign_in_at ? new Date(row.last_sign_in_at as string) : undefined,
        overallProgress,
      };
    });
  },

  async getById(id: string): Promise<Learner | null> {
    const { data, error } = await supabase
      .from('learners')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) {
      console.error('Error fetching learner:', error);
      throw new Error("Impossible de charger l'apprenant");
    }

    return data ? toLearner(data) : null;
  },

  /**
   * Get learner count stats for an organization
   */
  async getStatsByOrganization(organizationId: string): Promise<{
    total: number;
    active: number;
    inactive: number;
  }> {
    const { data, error } = await supabase
      .from('learners')
      .select('status')
      .eq('organization_id', organizationId);

    if (error) {
      console.error('Error fetching learner stats:', error);
      throw new Error('Impossible de charger les statistiques');
    }

    const learners = data || [];
    return {
      total: learners.length,
      active: learners.filter((l) => l.status === 'active').length,
      inactive: learners.filter((l) => l.status === 'inactive').length,
    };
  },

  async search(query: string, organizationId?: string): Promise<Learner[]> {
    let dbQuery = supabase
      .from('learners')
      .select('*')
      .or(`fullname.ilike.%${query}%,email.ilike.%${query}%,department.ilike.%${query}%`);

    if (organizationId) {
      dbQuery = dbQuery.eq('organization_id', organizationId);
    }

    const { data, error } = await dbQuery.order('fullname');

    if (error) {
      console.error('Error searching learners:', error);
      throw new Error('Erreur lors de la recherche');
    }

    return (data || []).map(toLearner);
  },
};
